import { diff3MergeRegions } from "node-diff3"
import { createHTMLComparisonArray } from "routes/schedule/compare/lib/diff3/utilities"
import { normalizeAuthoringPlainText } from "./authoring-comparison-normalization"
import {
    applyAuthoringRecommendationToDraft,
    AUTHORING_RECOMMENDATION_STATE_APPLIED,
    AUTHORING_RECOMMENDATION_STATE_READY,
    filterVisibleAuthoringRecommendations,
    getAuthoringRecommendationState,
} from "./authoring-schedule-recommendations"

export const AUTHORING_RECOMMENDATION_GROUP_STATE_READY = "ready"
export const AUTHORING_RECOMMENDATION_GROUP_STATE_APPLIED = "applied"
export const AUTHORING_RECOMMENDATION_GROUP_STATE_CONFLICTED = "conflicted"
export const AUTHORING_RECOMMENDATION_GROUP_STATE_CHOICE_REQUIRED = "choiceRequired"

const MERGEABLE_CHANGE_TYPES = new Set([
    "schedule_title",
    "intro_content",
    "intro_note",
    "task_title",
    "task_content",
    "task_note",
])

const RICH_TEXT_CHANGE_TYPES = new Set(["intro_content", "intro_note", "task_content", "task_note"])

export function buildAuthoringRecommendationDisplayItems({
    recommendations = [],
    draft,
    sourceFilter = "all",
    selectedRecommendationIdsByGroupKey = {},
}) {
    const visibleRecommendations = filterVisibleAuthoringRecommendations({ recommendations, draft })

    if (sourceFilter && sourceFilter !== "all") {
        return visibleRecommendations
    }

    return buildMergedAuthoringRecommendationGroups({
        recommendations: visibleRecommendations,
        draft,
        selectedRecommendationIdsByGroupKey,
    })
}

export function countAuthoringRecommendationDisplayItems({ items = [], draft }) {
    return items.reduce(
        (result, item) => {
            const state = resolveDisplayItemState({ item, draft })

            result.totalCount += 1

            if (state === AUTHORING_RECOMMENDATION_GROUP_STATE_APPLIED) {
                result.appliedCount += 1
            } else {
                result.unappliedCount += 1
            }

            if (state === AUTHORING_RECOMMENDATION_GROUP_STATE_READY) {
                result.readyCount += 1
            }

            if (state === AUTHORING_RECOMMENDATION_GROUP_STATE_CONFLICTED) {
                result.conflictedCount += 1
            }

            if (state === AUTHORING_RECOMMENDATION_GROUP_STATE_CHOICE_REQUIRED) {
                result.choiceRequiredCount += 1
            }

            return result
        },
        {
            totalCount: 0,
            unappliedCount: 0,
            appliedCount: 0,
            readyCount: 0,
            conflictedCount: 0,
            choiceRequiredCount: 0,
        }
    )
}

export function resolveAuthoringRecommendationDisplayItemApplyPayload({
    item,
    selectedRecommendationIdsByGroupKey = {},
}) {
    if (!item) {
        return { recommendation: null, noteRecommendations: [] }
    }

    if (item.kind !== "merged-group") {
        return {
            recommendation: item,
            noteRecommendations: [item],
        }
    }

    const selectedRecommendationId =
        selectedRecommendationIdsByGroupKey?.[item.groupKey] || item.selectedRecommendationId || ""

    if (item.groupState === AUTHORING_RECOMMENDATION_GROUP_STATE_CHOICE_REQUIRED) {
        const selectedRecommendation =
            item.items.find((recommendation) => recommendation?.id === selectedRecommendationId) ?? null

        return {
            recommendation: selectedRecommendation,
            noteRecommendations: selectedRecommendation ? [selectedRecommendation] : [],
        }
    }

    return {
        recommendation: item.previewRecommendation ?? null,
        noteRecommendations: Array.isArray(item.usedRecommendations) ? item.usedRecommendations : [],
    }
}

export function applyAuthoringRecommendationDisplayItemToDraft({
    item,
    draft,
    selectedRecommendationIdsByGroupKey = {},
    force = false,
}) {
    const payload = resolveAuthoringRecommendationDisplayItemApplyPayload({
        item,
        selectedRecommendationIdsByGroupKey,
    })
    if (!payload.recommendation) {
        return {
            applied: false,
            reason: "missing_recommendation",
            noteRecommendations: [],
        }
    }

    const result = applyAuthoringRecommendationToDraft({
        recommendation: payload.recommendation,
        draft,
        force,
    })

    return {
        ...result,
        recommendation: payload.recommendation,
        noteRecommendations: payload.noteRecommendations,
    }
}

/** Phase A: standalone recommendations and merge-key buckets (sync). */
export function partitionRecommendationsForMerge(recommendations = []) {
    const grouped = new Map()
    const standalone = []

    for (const recommendation of Array.isArray(recommendations) ? recommendations : []) {
        const mergeKey = buildMergeGroupKey(recommendation)
        if (!mergeKey) {
            standalone.push(recommendation)
            continue
        }

        const existing = grouped.get(mergeKey)
        if (existing) {
            existing.push(recommendation)
            continue
        }

        grouped.set(mergeKey, [recommendation])
    }

    return { standalone, grouped }
}

/** Phase B sync: append one merged group per merge bucket. */
export function appendMergedAuthoringRecommendationGroups({
    output,
    grouped,
    draft,
    selectedRecommendationIdsByGroupKey = {},
}) {
    for (const [groupKey, items] of grouped.entries()) {
        output.push(
            buildMergedAuthoringRecommendationGroup({
                groupKey,
                items,
                draft,
                selectedRecommendationIdsByGroupKey,
            })
        )
    }

    return output
}

function buildMergedAuthoringRecommendationGroups({
    recommendations = [],
    draft,
    selectedRecommendationIdsByGroupKey = {},
}) {
    const { standalone, grouped } = partitionRecommendationsForMerge(recommendations)
    const output = [...standalone]

    return appendMergedAuthoringRecommendationGroups({
        output,
        grouped,
        draft,
        selectedRecommendationIdsByGroupKey,
    })
}

/** Phase A + Phase B with optional yield after each merge group (all view). */
export async function buildMergedAuthoringRecommendationGroupsIdle({
    recommendations = [],
    draft,
    selectedRecommendationIdsByGroupKey = {},
    yieldIdle,
}) {
    const { standalone, grouped } = partitionRecommendationsForMerge(recommendations)
    const output = [...standalone]

    for (const [groupKey, items] of grouped.entries()) {
        output.push(
            buildMergedAuthoringRecommendationGroup({
                groupKey,
                items,
                draft,
                selectedRecommendationIdsByGroupKey,
            })
        )

        if (yieldIdle) {
            await yieldIdle()
        }
    }

    return output
}

function buildMergedAuthoringRecommendationGroup({ groupKey, items, draft, selectedRecommendationIdsByGroupKey = {} }) {
    const sortedItems = [...items].sort(sortRecommendationItemsWithinGroup)
    const recommendationStates = sortedItems.map((item) => ({
        item,
        state: getAuthoringRecommendationState({ recommendation: item, draft }),
    }))
    const readyItems = recommendationStates
        .filter((entry) => entry.state === AUTHORING_RECOMMENDATION_STATE_READY)
        .map((entry) => entry.item)
    const appliedItems = recommendationStates
        .filter((entry) => entry.state === AUTHORING_RECOMMENDATION_STATE_APPLIED)
        .map((entry) => entry.item)
    const mergeResult = resolveMergedGroupPreviewForDraft({
        sortedItems,
        readyItems,
        appliedItems,
        draft,
    })
    const selectedRecommendationId = resolveSelectedRecommendationId({
        groupKey,
        groupState: mergeResult.groupState,
        items: sortedItems,
        selectedRecommendationIdsByGroupKey,
    })
    const previewRecommendation = resolveGroupPreviewRecommendation({
        items: sortedItems,
        mergeResult,
        selectedRecommendationId,
    })
    const usedRecommendations = resolveUsedRecommendations({
        items: sortedItems,
        mergeResult,
        selectedRecommendationId,
    })

    return {
        kind: "merged-group",
        id: groupKey,
        groupKey,
        mergeTargetKind: sortedItems[0]?.mergeTargetKind ?? deriveMergeTargetKind(sortedItems[0]),
        mergeTargetId: sortedItems[0]?.mergeTargetId ?? deriveMergeTargetId(sortedItems[0]),
        mergeTargetLabel: sortedItems[0]?.mergeTargetLabel ?? sortedItems[0]?.targetLabel ?? "Recommendation",
        changeType: sortedItems[0]?.changeType ?? "",
        changeLabel: sortedItems[0]?.changeLabel ?? "Recommendation",
        targetLabel: sortedItems[0]?.mergeTargetLabel ?? sortedItems[0]?.targetLabel ?? "Recommendation",
        mergeMode: mergeResult.mergeMode,
        groupState: mergeResult.groupState,
        items: sortedItems,
        previewRecommendation,
        usedRecommendations,
        selectedRecommendationId,
        sourceBadges: Array.from(new Set(sortedItems.map((item) => item?.sourceLabel).filter(Boolean))),
        sourceCount: Array.from(new Set(sortedItems.map((item) => item?.sourceType).filter(Boolean))).length,
        sourcePriority: Math.min(...sortedItems.map((item) => Number(item?.sourcePriority ?? 99))),
        taskNumber: sortedItems[0]?.taskNumber ?? null,
        taskTitle: sortedItems[0]?.taskTitle ?? null,
        criticality: resolveGroupCriticality(sortedItems),
    }
}

function resolveMergedGroupPreviewForDraft({ sortedItems, readyItems, appliedItems, draft }) {
    if (readyItems.length > 0) {
        return resolveMergedGroupPreview({
            items: readyItems,
            defaultGroupState: AUTHORING_RECOMMENDATION_GROUP_STATE_READY,
        })
    }

    if (appliedItems.length > 0) {
        return resolveMergedGroupPreview({
            items: appliedItems,
            defaultGroupState: AUTHORING_RECOMMENDATION_GROUP_STATE_APPLIED,
        })
    }

    const mergedAppliedResult = resolveAppliedMergedGroupPreview({
        items: sortedItems,
        draft,
    })
    if (mergedAppliedResult) return mergedAppliedResult

    return {
        mergeMode: "conflicted",
        groupState: AUTHORING_RECOMMENDATION_GROUP_STATE_CONFLICTED,
        previewRecommendation: null,
    }
}

function resolveAppliedMergedGroupPreview({ items, draft }) {
    const mergedResult = resolveMergedGroupPreview({
        items,
        defaultGroupState: AUTHORING_RECOMMENDATION_GROUP_STATE_APPLIED,
    })
    if (!mergedResult?.previewRecommendation) return null

    const previewState = getAuthoringRecommendationState({
        recommendation: mergedResult.previewRecommendation,
        draft,
    })
    if (previewState !== AUTHORING_RECOMMENDATION_STATE_APPLIED) return null

    return {
        ...mergedResult,
        groupState: AUTHORING_RECOMMENDATION_GROUP_STATE_APPLIED,
    }
}

function resolveMergedGroupPreview({ items, defaultGroupState = AUTHORING_RECOMMENDATION_GROUP_STATE_READY }) {
    if (!items.length) {
        return {
            mergeMode: "conflicted",
            groupState: AUTHORING_RECOMMENDATION_GROUP_STATE_CONFLICTED,
            previewRecommendation: null,
        }
    }

    if (items.length === 1) {
        return {
            mergeMode: "consensus",
            groupState: defaultGroupState,
            previewRecommendation: items[0],
        }
    }

    if (allRecommendationsShareEffectiveNewText(items)) {
        return {
            mergeMode: "consensus",
            groupState: defaultGroupState,
            previewRecommendation: items[0],
        }
    }

    const composedRecommendation = composeRecommendations(items)
    if (composedRecommendation) {
        return {
            mergeMode: "composable",
            groupState: defaultGroupState,
            previewRecommendation: composedRecommendation,
        }
    }

    return {
        mergeMode: items.length ? "choiceRequired" : "conflicted",
        groupState: items.length
            ? AUTHORING_RECOMMENDATION_GROUP_STATE_CHOICE_REQUIRED
            : AUTHORING_RECOMMENDATION_GROUP_STATE_CONFLICTED,
        previewRecommendation: items[0] ?? null,
    }
}

function resolveGroupPreviewRecommendation({ items, mergeResult, selectedRecommendationId }) {
    if (mergeResult.mergeMode !== "choiceRequired") {
        return mergeResult.previewRecommendation
    }

    return items.find((item) => item?.id === selectedRecommendationId) ?? items[0] ?? null
}

function resolveUsedRecommendations({ items, mergeResult, selectedRecommendationId }) {
    if (mergeResult.mergeMode === "choiceRequired") {
        return items.filter((item) => item?.id === selectedRecommendationId)
    }

    if (mergeResult.groupState === AUTHORING_RECOMMENDATION_GROUP_STATE_CONFLICTED) {
        return []
    }

    return items
}

function resolveSelectedRecommendationId({ groupKey, groupState, items, selectedRecommendationIdsByGroupKey = {} }) {
    if (groupState !== AUTHORING_RECOMMENDATION_GROUP_STATE_CHOICE_REQUIRED) return ""

    const selectedRecommendationId = `${selectedRecommendationIdsByGroupKey?.[groupKey] || ""}`.trim()
    if (selectedRecommendationId && items.some((item) => item?.id === selectedRecommendationId)) {
        return selectedRecommendationId
    }

    return ""
}

function allRecommendationsShareEffectiveNewText(items) {
    const [first, ...rest] = items
    const baseline = normalizeRecommendationText(first?.changeType, first?.newText)
    if (!baseline) return false

    return rest.every((item) => normalizeRecommendationText(item?.changeType, item?.newText) === baseline)
}

function composeRecommendations(items) {
    const [first, ...rest] = items
    if (!first) return null

    const baseText = `${first?.oldText ?? ""}`
    const changeType = `${first?.changeType || ""}`

    if (!canAutoMergeChangeType(changeType)) return null
    if (
        !rest.every(
            (item) =>
                normalizeRecommendationText(changeType, item?.oldText) ===
                normalizeRecommendationText(changeType, baseText)
        )
    ) {
        return null
    }

    let mergedText = `${first?.newText ?? ""}`
    for (const item of rest) {
        const nextText = `${item?.newText ?? ""}`
        const merged = mergeRecommendationTexts({
            changeType,
            baseText,
            leftText: mergedText,
            rightText: nextText,
        })
        if (!merged) return null
        mergedText = merged
    }

    return {
        ...first,
        id: `merged:${buildMergeGroupKey(first)}`,
        sourceType: "merged",
        sourceLabel: "Merged",
        sourcePriority: Math.min(...items.map((item) => Number(item?.sourcePriority ?? 99))),
        newText: mergedText,
    }
}

function mergeRecommendationTexts({ changeType, baseText, leftText, rightText }) {
    const isRichText = RICH_TEXT_CHANGE_TYPES.has(changeType)
    const baseTokens = tokenizeForMerge(baseText, isRichText)
    const leftTokens = tokenizeForMerge(leftText, isRichText)
    const rightTokens = tokenizeForMerge(rightText, isRichText)
    const regions = diff3MergeRegions(leftTokens, baseTokens, rightTokens)

    if (regions.some((region) => !region?.stable)) {
        return null
    }

    const mergedTokens = regions.flatMap((region) => region?.bufferContent ?? [])

    if (isRichText) {
        return serializeMergedRichTextTokens(mergedTokens)
    }

    return mergedTokens.join("")
}

function tokenizeForMerge(value, isRichText) {
    if (isRichText) {
        return createHTMLComparisonArray(`${value ?? ""}`)
    }

    return Array.from(`${value ?? ""}`)
}

function serializeMergedRichTextTokens(tokens = []) {
    const html = tokens.join(" ")
    const wrapper = document.createElement("div")
    wrapper.innerHTML = html
    return wrapper.innerHTML
}

function resolveDisplayItemState({ item, draft }) {
    if (item?.kind === "merged-group") return item.groupState
    return getAuthoringRecommendationState({ recommendation: item, draft })
}

/** Recompute merged-group applied/ready state from the current in-memory draft. */
export function resolveMergedGroupDisplayState({ item, draft, selectedRecommendationIdsByGroupKey = {} }) {
    if (!item || item.kind !== "merged-group") return item?.groupState ?? null

    return buildMergedAuthoringRecommendationGroup({
        groupKey: item.groupKey,
        items: item.items,
        draft,
        selectedRecommendationIdsByGroupKey,
    }).groupState
}

function sortRecommendationItemsWithinGroup(left, right) {
    const sourceCompare = Number(left?.sourcePriority ?? 99) - Number(right?.sourcePriority ?? 99)
    if (sourceCompare !== 0) return sourceCompare
    return `${left?.id || ""}`.localeCompare(`${right?.id || ""}`)
}

function resolveGroupCriticality(items) {
    const order = { red: 0, amber: 1, green: 2 }
    const criticalities = items
        .map((item) => `${item?.criticality || ""}`.toLowerCase())
        .filter((value) => value in order)
        .sort((left, right) => order[left] - order[right])

    return criticalities[0] || null
}

function buildMergeGroupKey(recommendation = {}) {
    const mergeTargetKind = recommendation?.mergeTargetKind ?? deriveMergeTargetKind(recommendation)
    const mergeTargetId = recommendation?.mergeTargetId ?? deriveMergeTargetId(recommendation)
    if (!mergeTargetKind || !mergeTargetId) return ""
    return `${mergeTargetKind}:${mergeTargetId}`
}

function deriveMergeTargetKind(recommendation = {}) {
    const changeType = `${recommendation?.changeType || ""}`
    return MERGEABLE_CHANGE_TYPES.has(changeType) ? changeType : ""
}

function deriveMergeTargetId(recommendation = {}) {
    const changeType = `${recommendation?.changeType || ""}`
    if (!MERGEABLE_CHANGE_TYPES.has(changeType)) return ""
    if (changeType === "schedule_title") return "schedule"
    if (changeType === "intro_content" || changeType === "intro_note") return "intro"

    const taskIdentifier = `${recommendation?.resolvedTaskId || recommendation?.taskId || ""}`.trim()
    if (taskIdentifier) return `task:${taskIdentifier}`

    const taskNumber = Number(recommendation?.taskNumber)
    if (Number.isInteger(taskNumber) && taskNumber > 0) return `taskNumber:${taskNumber}`

    return ""
}

function canAutoMergeChangeType(changeType) {
    return MERGEABLE_CHANGE_TYPES.has(`${changeType || ""}`)
}

function normalizeRecommendationText(changeType, value) {
    if (RICH_TEXT_CHANGE_TYPES.has(`${changeType || ""}`)) {
        return normalizeAuthoringPlainText(value)
    }

    return `${value ?? ""}`.trim().replace(/\s+/g, " ").toLowerCase()
}
